import React, { useMemo, useState } from 'react'
import Modal from './Modal'
import RosterTable, { rosterTotals } from './RosterTable'
import { C, ui } from '../theme'

const h = ui.h
const input = ui.input
const btn = ui.btn
const row = ui.row

// The single troop-raising surface: pick a style, a target, type counts against the catalog,
// see the cost, submit. Replaces the old sidebar "New army" + "Recruit" sections.
//   instant   — free vs. the faction's DM-set budget; materializes immediately
//   mercenary — 100 days' wages signing bonus, 5-day train
//   regular   — 20 days' wages signing bonus, 30-day train
const STYLES = [
  { key: 'instant', label: 'Instant / Starting', blurb: 'Free, immediate — drawn from the faction’s starting budget.' },
  { key: 'mercenary', label: 'Mercenaries', blurb: '100 days’ wages signing bonus · 5-day muster.' },
  { key: 'regular', label: 'Regulars', blurb: '20 days’ wages signing bonus · 30-day muster.' },
]
const BONUS_DAYS = { mercenary: 100, regular: 20 }

export default function RecruitModal({ isDm, factions, myFactionId, armies, catalog = [], musterCities, recruitments = [], onRecruit, onClose }) {
  const [factionId, setFactionId] = useState(isDm ? (factions[0] && factions[0].id) : myFactionId)
  const [style, setStyle] = useState('instant')
  const [targetMode, setTargetMode] = useState('new') // new | army
  const [name, setName] = useState('')
  const [cityIdx, setCityIdx] = useState(0)
  const [targetArmyId, setTargetArmyId] = useState(null)
  const [counts, setCounts] = useState({})
  const [busy, setBusy] = useState(false)

  const recruitFactionId = isDm ? factionId : myFactionId
  const faction = factions.find(f => f.id === recruitFactionId)
  const cities = musterCities ? musterCities(recruitFactionId) : []
  const myArmies = armies.filter(a => a.faction_id === recruitFactionId)
  const myRecruitments = recruitments.filter(r => r.faction_id === recruitFactionId)

  const totals = useMemo(() => rosterTotals(catalog, counts), [catalog, counts])
  const cost = style === 'instant' ? totals.wage : (BONUS_DAYS[style] || 0) * totals.wage
  const budget = faction ? (faction.instant_budget_gp || 0) : 0
  const treasury = faction ? (faction.treasury || 0) : 0
  const overBudget = style === 'instant' && cost > budget
  const overTreasury = style !== 'instant' && cost > treasury

  const targetOk = targetMode === 'army' ? !!targetArmyId : (cities.length > 0 && !!name.trim())
  const canSubmit = totals.men > 0 && targetOk && !overBudget && !overTreasury && !busy

  const submit = async () => {
    if (!canSubmit) return
    const rows = catalog.map(t => ({ troopCode: t.code, men: Number(counts[t.code]) || 0 })).filter(r => r.men > 0)
    const target = targetMode === 'army'
      ? { mode: 'army', armyId: Number(targetArmyId) }
      : { mode: 'new', name: name.trim(), row: cities[cityIdx].row, col: cities[cityIdx].col }
    setBusy(true)
    const res = await onRecruit({ factionId: recruitFactionId, style, target, rows })
    setBusy(false)
    if (res && res.ok) onClose()
  }

  // Two responsive columns: controls (faction/style/target) left, roster + cost + action right.
  // flexWrap + flex-basis lets the columns sit side by side when there's room and stack on
  // narrow screens; the Modal itself is width-capped to the viewport.
  const cols = { display: 'flex', gap: 20, flexWrap: 'wrap', alignItems: 'flex-start' }
  const leftCol = { flex: '1 1 260px', minWidth: 240 }
  const rightCol = { flex: '1 1 320px', minWidth: 280 }

  return (
    <Modal title="Recruit troops" onClose={onClose} width={820}>
      <div style={cols}>
        {/* ── left: who / how / where ── */}
        <div style={leftCol}>
          {isDm && (
            <select style={input} value={factionId} onChange={e => { setFactionId(Number(e.target.value)); setTargetArmyId(null); setCityIdx(0) }}>
              {factions.map(f => <option key={f.id} value={f.id}>{f.name}</option>)}
            </select>
          )}

          <div style={{ ...h, marginTop: isDm ? 6 : 0 }}>Style</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, marginBottom: 6 }}>
            {STYLES.map(s => (
              <button key={s.key} onClick={() => setStyle(s.key)}
                style={{ padding: '8px 10px', borderRadius: 5, cursor: 'pointer', fontSize: 12.5, textAlign: 'left',
                  border: `1px solid ${style === s.key ? C.gold : C.rule}`,
                  background: style === s.key ? C.highlight : C.paperRaised,
                  color: C.ink, fontFamily: "'Cinzel', serif", letterSpacing: 0.3 }}>{s.label}</button>
            ))}
          </div>
          <div style={{ fontSize: 11.5, opacity: 0.7, marginBottom: 2 }}>{STYLES.find(s => s.key === style).blurb}</div>
          {style === 'instant' && (
            <div style={{ fontSize: 12, color: overBudget ? C.crimson : C.inkSoft }}>
              Budget remaining: <b>{Math.round(budget).toLocaleString()}</b> gp
            </div>
          )}

          <div style={h}>Target</div>
          <div style={{ ...row, marginBottom: 6 }}>
            <button style={{ flex: 1, ...(targetMode === 'new' ? btn : ui.btnGhost) }} onClick={() => setTargetMode('new')}>New army</button>
            <button style={{ flex: 1, ...(targetMode === 'army' ? btn : ui.btnGhost) }} onClick={() => setTargetMode('army')} disabled={!myArmies.length}>Into existing</button>
          </div>
          {targetMode === 'new' ? (
            <>
              <input style={input} placeholder="Army name" value={name} onChange={e => setName(e.target.value)} />
              {cities.length === 0
                ? <div style={{ opacity: 0.6, fontSize: 12 }}>No controlled city to muster at.</div>
                : <select style={input} value={cityIdx} onChange={e => setCityIdx(Number(e.target.value))}>
                    {cities.map((c, i) => <option key={i} value={i}>Muster at {c.name || `city (${c.row},${c.col})`}</option>)}
                  </select>}
            </>
          ) : (
            <select style={input} value={targetArmyId ?? ''} onChange={e => setTargetArmyId(e.target.value)}>
              <option value="">army…</option>
              {myArmies.map(a => <option key={a.id} value={a.id}>{a.name}{a.tile_row != null ? ` (${a.tile_name || `${a.tile_row},${a.tile_col}`})` : ''}</option>)}
            </select>
          )}

          {myRecruitments.length > 0 && (
            <>
              <div style={h}>In training ({myRecruitments.length})</div>
              {myRecruitments.map(r => (
                <div key={r.id} style={{ fontSize: 12, padding: '3px 0', opacity: 0.85 }}>
                  · {r.men} {r.troop_name} ({r.type}) — {r.status === 'ready' ? 'ready' : `ready day ${r.ready_day}`}
                  {r.target_army_name ? ` → ${r.target_army_name}` : (r.muster_row != null ? ` @ ${r.muster_name || `(${r.muster_row},${r.muster_col})`}` : '')}
                </div>
              ))}
            </>
          )}
        </div>

        {/* ── right: roster + cost + action ── */}
        <div style={rightCol}>
          <div style={{ ...h, marginTop: 0 }}>Roster — type a count for each unit</div>
          {/* grow with the viewport so a tall monitor shows the whole catalog without scrolling;
              still caps + scrolls on short screens */}
          <RosterTable rows={catalog} counts={counts} onChange={(code, v) => setCounts(c => ({ ...c, [code]: v }))} maxHeight="72vh" />
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 12.5 }}>
            <span><b>{totals.men.toLocaleString()}</b> men</span>
            <span style={{ opacity: 0.8 }}>· {Math.round(totals.wage).toLocaleString()} gp/day wages</span>
            <span style={{ marginLeft: 'auto', color: (overBudget || overTreasury) ? C.crimson : C.gold, fontWeight: 600 }}>
              {style === 'instant' ? 'Budget cost' : 'Signing bonus'}: {Math.round(cost).toLocaleString()} gp
            </span>
          </div>
          {(overBudget || overTreasury) && (
            <div style={{ fontSize: 12, color: C.crimson, marginTop: 4 }}>
              {overBudget ? 'Exceeds the faction’s instant-recruitment budget.' : 'Exceeds the faction treasury.'}
            </div>
          )}
          <button style={{ ...btn, width: '100%', marginTop: 10, opacity: canSubmit ? 1 : 0.5 }} disabled={!canSubmit} onClick={submit}>
            {style === 'instant' ? 'Raise troops' : 'Begin mustering'}
          </button>
        </div>
      </div>
    </Modal>
  )
}
